import React from 'react';
import { Field, Label, Description, ErrorMessage } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Textarea } from '@oysterjs/ui/textarea';
import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TriggerEmailValueMode } from '~/api/graphql';
import { EMAIL_LIMITS, type EmailErrors } from '~/api/data/triggers/email-config';

type ControlProps = {
  id: string;
  invalid: boolean;
  'aria-invalid': boolean;
  'aria-labelledby': string;
  'aria-describedby'?: string;
};
export const BuilderField = ({
  label,
  path,
  errors,
  description,
  children
}: {
  label: string;
  path: string;
  errors: EmailErrors;
  description?: string;
  children: (props: ControlProps) => React.ReactNode;
}) => {
  const id = React.useId();
  const error = errors[path];
  return (
    <Field data-email-field={path}>
      <Label id={`${id}-label`} htmlFor={id}>
        {label}
      </Label>
      {description && <Description id={`${id}-description`}>{description}</Description>}
      {children({
        id,
        invalid: !!error,
        'aria-invalid': !!error,
        'aria-labelledby': `${id}-label`,
        'aria-describedby':
          [description ? `${id}-description` : '', error ? `${id}-error` : '']
            .filter(Boolean)
            .join(' ') || undefined
      })}
      {error && <ErrorMessage id={`${id}-error`}>{error}</ErrorMessage>}
    </Field>
  );
};
/** Helper text under a control or card: muted by default, red when it reports a problem. */
export const Help = ({
  error,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'p'> & { error?: boolean }) => (
  <p
    {...props}
    className={`m-0 text-sm/6 ${
      error ? 'text-red-600 dark:text-red-400' : 'text-neutral-500 dark:text-neutral-400'
    } ${className ?? ''}`}
  />
);
export const PathErrors = ({ errors, path }: { errors: EmailErrors; path: string }) => (
  <>
    {Object.entries(errors)
      .filter(([key]) => key === path || key === `${path}.id` || key === `${path}.kind`)
      .map(([key, message]) => (
        <p key={key} className="text-sm text-red-600 dark:text-red-400" role="alert">
          {message}
        </p>
      ))}
  </>
);
export const Check = ({
  label,
  description,
  checked,
  onChange,
  disabled = false
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) => (
  <CheckboxField>
    <Checkbox color="sky" checked={checked} disabled={disabled} onChange={onChange} />
    <Label>{label}</Label>
    {description && <Description>{description}</Description>}
  </CheckboxField>
);
export const humanize = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
export const toggleValue = <T,>(values: T[], value: T, checked: boolean) =>
  checked ? [...values, value] : values.filter((item) => item !== value);

// Each row owns one literal value; regex and multiline text are never delimiter-parsed.
export const ValuesField = ({
  values,
  onChange,
  path,
  errors,
  label = 'Values',
  description
}: {
  values: string[];
  onChange: (values: string[]) => void;
  path: string;
  errors: EmailErrors;
  label?: string;
  description?: string;
}) => (
  <div className="space-y-2">
    <BuilderField
      label={label}
      path={path}
      errors={errors}
      description={`One value per row (up to ${EMAIL_LIMITS.values}). Commas and line breaks are kept as part of the value.${description ? ` ${description}` : ''}`}
    >
      {(props) => (
        <div
          id={props.id}
          role="group"
          aria-labelledby={props['aria-labelledby']}
          aria-invalid={props['aria-invalid']}
          aria-describedby={props['aria-describedby']}
        >
          <div className="space-y-2">
            {values.map((value, index) => (
              <div className="flex items-start gap-2" key={index}>
                <Field className="min-w-0 flex-1">
                  <Label className="sr-only">{`${label} ${index + 1}`}</Label>
                  <Textarea
                    invalid={!!errors[`${path}.${index}`]}
                    rows={1}
                    value={value}
                    onChange={(event) =>
                      onChange(values.map((item, i) => (i === index ? event.target.value : item)))
                    }
                  />
                  {errors[`${path}.${index}`] && (
                    <ErrorMessage>{errors[`${path}.${index}`]}</ErrorMessage>
                  )}
                </Field>
                <Button
                  plain
                  type="button"
                  aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <XMarkIcon />
                </Button>
              </div>
            ))}
          </div>
          <Button
            plain
            type="button"
            disabled={values.length >= EMAIL_LIMITS.values}
            onClick={() => onChange([...values, ''])}
          >
            <PlusIcon />
            Add value
          </Button>
        </div>
      )}
    </BuilderField>
  </div>
);
export const ValueModeField = ({
  value,
  onChange,
  path,
  errors
}: {
  value?: TriggerEmailValueMode | null;
  onChange: (value: TriggerEmailValueMode) => void;
  path: string;
  errors: EmailErrors;
}) => (
  <BuilderField label="Match values" path={path} errors={errors}>
    {(props) => (
      <Select
        {...props}
        value={value ?? TriggerEmailValueMode.Any}
        onChange={(event) => onChange(event.target.value as TriggerEmailValueMode)}
      >
        <option value={TriggerEmailValueMode.Any}>Any value: at least one value</option>
        <option value={TriggerEmailValueMode.All}>All values: every value</option>
      </Select>
    )}
  </BuilderField>
);
export const NumberField = ({
  value,
  onChange,
  path,
  errors,
  label,
  nullable = true,
  max
}: {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  path: string;
  errors: EmailErrors;
  label: string;
  nullable?: boolean;
  max?: number;
}) => (
  <BuilderField label={label} path={path} errors={errors}>
    {(props) => (
      <Input
        {...props}
        type="number"
        min={0}
        max={max ?? Number.MAX_SAFE_INTEGER}
        step={1}
        value={value != null && Number.isFinite(value) ? value : ''}
        onChange={(event) =>
          onChange(
            event.target.value === '' ? (nullable ? null : Number.NaN) : event.target.valueAsNumber
          )
        }
      />
    )}
  </BuilderField>
);
export const minuteText = (minute: number) =>
  Number.isInteger(minute) && minute >= 0 && minute <= 1440
    ? `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
    : '';
export const parseMinute = (text: string) => {
  if (!/^\d{2}:\d{2}$/.test(text)) return -1;
  const hour = Number(text.slice(0, 2)),
    minute = Number(text.slice(3));
  return hour <= 23 && minute <= 59 ? hour * 60 + minute : -1;
};
